import type { SupportedByKeyMap, SupportedMethod } from './types';

export const normalizeSupportedMethodId = (m: SupportedMethod): string => {
  return String(m.methodId || m.id || '').trim();
};

export const formatProviderDisplayName = (providerRaw: string): string => {
  const provider = (providerRaw || '').trim().toLowerCase();
  if (provider === 'online_debt') return 'Internal balance';
  if (provider === 'zeroxprocessing') return '0xProcessing';
  if (provider === 'linkmepay') return 'LinkMePay';
  if (provider === 'ggusonepay') return 'GGUSOnePay';
  if (provider === 'btcpay') return 'BTCPay';
  if (provider === 'meld') return 'Meld';
  return providerRaw || provider;
};

export const formatOnlineDebtMethodLabel = (methodIdRaw: string): string => {
  const methodId = String(methodIdRaw || '').trim().toLowerCase();
  if (methodId === 'internal' || methodId === 'all') return 'Internal balance';
  return 'Internal balance';
};

const BTCPAY_WITHDRAWAL_METHOD_LABELS: Record<string, { name: string; ticker: string }> = {
  bitcoin_transfer: { name: 'Bitcoin On-Chain', ticker: 'BTC' },
  litecoin_transfer: { name: 'Litecoin', ticker: 'LTC' },
  dogecoin_transfer: { name: 'Dogecoin', ticker: 'DOGE' },
};

export const formatBtcpayWithdrawalMethodLabel = (methodIdRaw: string): string => {
  const methodId = String(methodIdRaw || '').trim().toLowerCase();
  const mapped = BTCPAY_WITHDRAWAL_METHOD_LABELS[methodId];
  if (mapped) return mapped.name;
  return methodIdRaw || 'Method';
};

/** Player-facing gateway label so GGUS Cash App is distinct from LinkMePay. */
export const GGUSONEPAY_CARD_SUBTITLE = 'OnePay';

const GGUSONEPAY_METHOD_LABELS: Record<string, string> = {
  cashapp: 'Cash App',
  chime: 'Chime',
  applepay: 'Apple Pay',
  googlepay: 'Google Pay',
  ggus_btcpay: 'BTC Lightning (Cash App)',
  cashapp_transfer: 'Cash App',
  chime_transfer: 'Chime',
};

export function formatGgusonepayMethodLabel(methodIdRaw: string): string | null {
  const methodId = String(methodIdRaw || '').trim().toLowerCase();
  return GGUSONEPAY_METHOD_LABELS[methodId] ?? null;
}

/** Fill missing catalog keys from hardcoded rails (agent / super-agent fee view). */
export function fillHardcodedSupportedCatalog(catalog: SupportedByKeyMap): SupportedByKeyMap {
  const out: SupportedByKeyMap = { ...(catalog || {}) };
  for (const key of Object.keys(HARD_CODED_SUPPORTED_METHODS_BY_KEY)) {
    const [provider, operation] = key.split('::');
    out[key] = mergeWithHardcodedSupportedMethods(provider, out[key] || [], operation);
  }
  return out;
}

const HARD_CODED_SUPPORTED_METHODS_BY_KEY: Record<string, SupportedMethod[]> = {
  'linkmepay::deposit': [
    { methodId: 'ecash_app', name: 'ECash App', ticker: 'ECASH' },
    { methodId: 'paypal', name: 'PayPal', ticker: 'PAYPAL' },
  ],
  'ggusonepay::deposit': [
    { methodId: 'cashapp', name: 'Cash App', ticker: 'CASHAPP' },
    { methodId: 'chime', name: 'Chime', ticker: 'CHIME' },
    { methodId: 'applepay', name: 'Apple Pay', ticker: 'APPLEPAY' },
    { methodId: 'googlepay', name: 'Google Pay', ticker: 'GOOGLEPAY' },
    { methodId: 'ggus_btcpay', name: 'BTC Lightning (Cash App)', ticker: 'BTC' },
  ],
  'ggusonepay::withdrawal': [
    { methodId: 'cashapp_transfer', name: 'Cash App', ticker: 'CASHAPP' },
    { methodId: 'chime_transfer', name: 'Chime', ticker: 'CHIME' },
  ],
  'btcpay::deposit': [
    { methodId: 'btc_onchain', name: 'Bitcoin On-Chain', ticker: 'BTC' },
    { methodId: 'ltc_chain', name: 'Litecoin', ticker: 'LTC' },
    { methodId: 'doge_chain', name: 'Dogecoin', ticker: 'DOGE' },
  ],
  'btcpay::withdrawal': [
    { methodId: 'bitcoin_transfer', name: 'Bitcoin On-Chain', ticker: 'BTC' },
    { methodId: 'litecoin_transfer', name: 'Litecoin', ticker: 'LTC' },
    { methodId: 'dogecoin_transfer', name: 'Dogecoin', ticker: 'DOGE' },
  ],
};

export const mergeWithHardcodedSupportedMethods = (
  providerRaw: string,
  methods: SupportedMethod[],
  operationRaw = ''
): SupportedMethod[] => {
  const provider = (providerRaw || '').trim().toLowerCase();
  const operation = String(operationRaw || '').trim().toLowerCase();
  const hardcoded =
    HARD_CODED_SUPPORTED_METHODS_BY_KEY[`${provider}::${operation}`] ||
    HARD_CODED_SUPPORTED_METHODS_BY_KEY[`${provider}::`] ||
    [];

  const out: SupportedMethod[] = [];
  const seen = new Set<string>();

  const push = (m: SupportedMethod) => {
    const id = normalizeSupportedMethodId(m).trim().toLowerCase();
    if (!id || seen.has(id)) return;
    seen.add(id);
    out.push(m);
  };

  (Array.isArray(methods) ? methods : []).forEach(push);
  hardcoded.forEach(push);

  return out;
};

export const formatZeroxMethodIdLabel = (methodIdRaw: string): string => {
  const id = String(methodIdRaw || '').trim().toLowerCase();
  if (!id.startsWith('zerox_')) return methodIdRaw || 'Method';
  const rest = id.replace(/^zerox_/, '');
  if (rest === 'eth') return 'ETH';
  if (rest === 'usdt') return 'USDT';
  if (rest.startsWith('eth_')) {
    const network = rest
      .slice(4)
      .split('_')
      .filter(Boolean)
      .map((part) => part.toUpperCase())
      .join(' ');
    return network ? `ETH (${network})` : 'ETH';
  }
  if (rest.startsWith('usdt_')) {
    const network = rest
      .slice(5)
      .split('_')
      .filter(Boolean)
      .map((part) => part.toUpperCase())
      .join(' ');
    return network ? `USDT (${network})` : 'USDT';
  }
  return rest.toUpperCase();
};

export const prettyMethodLabel = (m: SupportedMethod): string => {
  const methodId = normalizeSupportedMethodId(m);
  const displayName = String(m.name || m.currency || '').trim();
  const ticker = String(m.ticker || '').trim();

  if (displayName.includes('(')) return displayName;
  if (displayName && ticker && displayName.length > ticker.length) return displayName;

  const ggusLabel = formatGgusonepayMethodLabel(methodId);
  if (ggusLabel) return ggusLabel;

  const t = (ticker || displayName).toUpperCase();
  const inferredNetwork =
    t === 'BTC'
      ? 'Bitcoin Network'
      : t === 'LTC'
        ? 'Litecoin Network'
        : t === 'DOGE'
          ? 'Dogecoin Network'
          : t
            ? `${t} Network`
            : '';
  if (ticker && inferredNetwork) return `${ticker} — ${inferredNetwork}`;
  if (displayName) return displayName;
  if (ticker) return ticker;
  if (methodId.startsWith('zerox_')) return formatZeroxMethodIdLabel(methodId);
  if (BTCPAY_WITHDRAWAL_METHOD_LABELS[methodId]) return formatBtcpayWithdrawalMethodLabel(methodId);
  return methodId || 'Method';
};

export const splitCoinAndNetworkLabel = (
  methodLabel: string,
  methodId = ''
): { coin: string; network: string } => {
  const label = String(methodLabel || '').trim();
  const id = String(methodId || '').trim().toLowerCase();

  const parenMatch = label.match(/^([^(]+)\(([^)]+)\)\s*$/);
  if (parenMatch) {
    return { coin: parenMatch[1].trim(), network: parenMatch[2].trim() };
  }

  const split = label.split(/\s*[-—–/|]\s*|\s{2,}/);
  if (split.length >= 2 && split[1]?.trim()) {
    return { coin: split[0].trim(), network: split[1].trim() };
  }

  if (id.startsWith('zerox_eth_')) {
    return {
      coin: 'ETH',
      network: id
        .replace('zerox_eth_', '')
        .split('_')
        .filter(Boolean)
        .map((p) => p.toUpperCase())
        .join(' '),
    };
  }
  if (id.startsWith('zerox_usdt_')) {
    return {
      coin: 'USDT',
      network: id
        .replace('zerox_usdt_', '')
        .split('_')
        .filter(Boolean)
        .map((p) => p.toUpperCase())
        .join(' '),
    };
  }
  if (id === 'zerox_eth') return { coin: 'ETH', network: 'Default' };
  if (id === 'zerox_usdt') return { coin: 'USDT', network: 'Default' };

  return { coin: label || '—', network: '' };
};

export const toPercentString = (feeRate: number): string => {
  const n = Number(feeRate);
  if (!Number.isFinite(n) || n < 0) return '0';
  const pct = n * 100;
  const rounded = Math.round(pct * 100 + Number.EPSILON) / 100;
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(rounded);
};

export const toRateNumber = (feePercent: string): number => {
  const p = Number(String(feePercent || '').trim());
  if (!Number.isFinite(p) || p < 0) return 0;
  return p / 100;
};

export const toMoneyString = (amount: number): string => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n < 0) return '0';
  return new Intl.NumberFormat('en-US', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(n);
};

export const normalizeNetworkFeeType = (raw: unknown): import('./types').NetworkFeeType => {
  const t = String(raw || 'none').trim().toLowerCase();
  if (t === 'percent' || t === 'fixed') return t;
  return 'none';
};

export const formatNetworkFeeDisplay = (
  type: import('./types').NetworkFeeType,
  percent: string,
  fixedUsd: string
): string => {
  if (type === 'percent') return `${percent || '0'}%`;
  if (type === 'fixed') return `$${fixedUsd || '0'}`;
  return '—';
};

export const networkFeeFieldsFromItem = (it: {
  networkFeeType?: string;
  networkFeeRate?: number;
  networkFeeAmount?: number;
}) => {
  const networkFeeType = normalizeNetworkFeeType(it.networkFeeType);
  return {
    networkFeeType,
    networkFeePercent: networkFeeType === 'percent' ? toPercentString(it.networkFeeRate ?? 0) : '0',
    networkFeeFixedUsd:
      networkFeeType === 'fixed' ? toMoneyString(Number(it.networkFeeAmount ?? 0)) : '0',
  };
};

export const toSupportedKey = (provider: string, operation: string) =>
  `${(provider || '').trim().toLowerCase()}::${String(operation || '').trim().toLowerCase()}`;

export function providerFeeRowId(row: { provider: string; operation: string; methodId: string }): string {
  return `${(row.provider || '').toLowerCase()}::${row.operation}::${(row.methodId || 'all').toLowerCase()}`;
}

export function getCoinVisual(methodId: string, methodLabel: string): {
  symbol: string;
  colorClass: string;
  textClass: string;
} {
  const id = methodId.toLowerCase();
  const label = methodLabel.toLowerCase();
  const isBtc = id.includes('btc') || id.includes('bitcoin') || label.includes('bitcoin');
  const isLtc = id.includes('ltc') || id.includes('litecoin') || label.includes('litecoin');
  const isDoge = id.includes('doge') || id.includes('dogecoin') || label.includes('dogecoin');
  const isEth = id.includes('eth') || id.includes('erc') || label.includes('ethereum');
  const isUsdt = id.includes('usdt') || id.includes('trc') || label.includes('tether');
  const isPaypal = id.includes('paypal') || label.includes('paypal');
  const isCashApp = id.includes('cash_app') || id.includes('cashapp') || id.includes('ecash') || label.includes('cash app');
  const isChime = id.includes('chime') || label.includes('chime');
  const isApplePay = id.includes('apple') || label.includes('apple pay');
  const isGooglePay = id.includes('google') || label.includes('google pay');
  const isCard = id.includes('card') || id.includes('credit') || id.includes('meld') || label.includes('card');
  if (isBtc) return { symbol: '₿', colorClass: 'bg-amber-500/20', textClass: 'text-amber-700' };
  if (isLtc) return { symbol: 'Ł', colorClass: 'bg-slate-500/20', textClass: 'text-slate-700' };
  if (isDoge) return { symbol: 'Ð', colorClass: 'bg-yellow-500/20', textClass: 'text-yellow-700' };
  if (isEth) return { symbol: 'Ξ', colorClass: 'bg-indigo-500/20', textClass: 'text-indigo-700' };
  if (isUsdt) return { symbol: 'T', colorClass: 'bg-emerald-500/20', textClass: 'text-emerald-700' };
  if (isPaypal) return { symbol: 'P', colorClass: 'bg-blue-500/20', textClass: 'text-blue-700' };
  if (isCashApp) return { symbol: '$', colorClass: 'bg-green-500/20', textClass: 'text-green-700' };
  if (isChime) return { symbol: 'C', colorClass: 'bg-cyan-500/20', textClass: 'text-cyan-700' };
  if (isApplePay) return { symbol: 'A', colorClass: 'bg-stone-800/20', textClass: 'text-stone-800' };
  if (isGooglePay) return { symbol: 'G', colorClass: 'bg-blue-500/20', textClass: 'text-blue-700' };
  if (isCard) return { symbol: 'C', colorClass: 'bg-purple-500/20', textClass: 'text-purple-700' };
  return { symbol: '•', colorClass: 'bg-stone-500/20', textClass: 'text-stone-600' };
}

export const SHOP_MIN_DEPOSIT_METHOD_MESSAGE =
  'You need to have at least 1 payment method for Players to deposit online.';

export const SHOP_MIN_WITHDRAWAL_METHOD_MESSAGE =
  'You need to have at least 1 payment method for Players to withdraw online.';

/** Mirror backend shopProviderDepositMethodGuard catalog keys. */
const SHOP_DEPOSIT_FEE_PROVIDERS = ['btcpay', 'linkmepay', 'ggusonepay', 'meld', 'zeroxprocessing'] as const;
const SHOP_WITHDRAWAL_FEE_PROVIDERS = ['btcpay', 'linkmepay', 'ggusonepay', 'zeroxprocessing'] as const;

export function isShopMinMethodGuardMessage(message: unknown): message is string {
  if (typeof message !== 'string') return false;
  const trimmed = message.trim();
  const normalized = trimmed.toLowerCase();
  return (
    trimmed === SHOP_MIN_DEPOSIT_METHOD_MESSAGE ||
    trimmed === SHOP_MIN_WITHDRAWAL_METHOD_MESSAGE ||
    (normalized.includes('at least 1 payment method') && normalized.includes('deposit online')) ||
    (normalized.includes('at least 1 payment method') && normalized.includes('withdraw online'))
  );
}

function catalogHasMethodsForOperation(
  supportedByKey: SupportedByKeyMap | undefined,
  operation: 'deposit' | 'withdrawal',
  providers: readonly string[]
): boolean {
  if (!supportedByKey) return false;
  return providers.some((provider) => {
    const methods = supportedByKey[toSupportedKey(provider, operation)] || [];
    return methods.length > 0;
  });
}

function rowInShopFeeCatalog(
  row: { provider: string; operation: string; methodId: string },
  supportedByKey: SupportedByKeyMap,
  providers: readonly string[]
): boolean {
  const provider = String(row.provider || '').trim().toLowerCase();
  const operation = String(row.operation || '').trim().toLowerCase();
  const methodId = String(row.methodId || '').trim().toLowerCase();
  if (!methodId || methodId === 'all') return false;
  if (!providers.includes(provider)) return false;
  if (operation !== 'deposit' && operation !== 'withdrawal') return false;
  const methods = supportedByKey[toSupportedKey(provider, operation)] || [];
  return methods.some(
    (m) => String(m.methodId || m.id || '').trim().toLowerCase() === methodId
  );
}

/** Count only catalog-backed rows — same scope as backend min-method guard. */
export function filterRowsForShopMinMethodGuard<
  T extends { provider: string; operation: string; methodId: string },
>(rows: T[], supportedByKey: SupportedByKeyMap | undefined, operation: 'deposit' | 'withdrawal'): T[] {
  const providers = operation === 'deposit' ? SHOP_DEPOSIT_FEE_PROVIDERS : SHOP_WITHDRAWAL_FEE_PROVIDERS;
  const matchesOperation = operation === 'deposit' ? isDepositOperation : isWithdrawalOperation;
  const opRows = rows.filter((row) => matchesOperation(row.operation));
  if (!catalogHasMethodsForOperation(supportedByKey, operation, providers)) {
    return opRows;
  }
  return opRows.filter((row) => rowInShopFeeCatalog(row, supportedByKey!, providers));
}

export function isDepositOperation(operation: string): boolean {
  return String(operation || '').trim().toLowerCase() === 'deposit';
}

export function isWithdrawalOperation(operation: string): boolean {
  return String(operation || '').trim().toLowerCase() === 'withdrawal';
}

export function countEnabledDepositMethods(
  rows: Array<{ operation: string; isEnabled?: boolean }>
): number {
  return rows.filter((row) => isDepositOperation(row.operation) && row.isEnabled !== false).length;
}

export function countEnabledWithdrawalMethods(
  rows: Array<{ operation: string; isEnabled?: boolean }>
): number {
  return rows.filter((row) => isWithdrawalOperation(row.operation) && row.isEnabled !== false).length;
}

function wouldLeaveNoEnabledMethodsForOperation(
  operation: 'deposit' | 'withdrawal',
  rows: Array<{ provider: string; operation: string; methodId: string; isEnabled?: boolean }>,
  changes: Array<{ provider: string; operation: string; methodId: string; isEnabled?: boolean }>
): boolean {
  const matchesOperation =
    operation === 'deposit' ? isDepositOperation : isWithdrawalOperation;
  if (!changes.some((change) => matchesOperation(change.operation) && change.isEnabled === false)) {
    return false;
  }
  const changeByKey = new Map(
    changes.map((change) => [
      `${String(change.provider || '').toLowerCase()}::${String(change.operation || '').toLowerCase()}::${String(change.methodId || '').toLowerCase()}`,
      change.isEnabled !== false,
    ])
  );
  let enabledCount = 0;
  for (const row of rows) {
    if (!matchesOperation(row.operation)) continue;
    const key = `${String(row.provider || '').toLowerCase()}::${String(row.operation || '').toLowerCase()}::${String(row.methodId || '').toLowerCase()}`;
    const isEnabled = changeByKey.has(key) ? changeByKey.get(key)! : row.isEnabled !== false;
    if (isEnabled) enabledCount += 1;
  }
  return enabledCount < 1;
}

export function wouldLeaveNoEnabledDepositMethods(
  rows: Array<{ provider: string; operation: string; methodId: string; isEnabled?: boolean }>,
  changes: Array<{ provider: string; operation: string; methodId: string; isEnabled?: boolean }>
): boolean {
  return wouldLeaveNoEnabledMethodsForOperation('deposit', rows, changes);
}

export function wouldLeaveNoEnabledWithdrawalMethods(
  rows: Array<{ provider: string; operation: string; methodId: string; isEnabled?: boolean }>,
  changes: Array<{ provider: string; operation: string; methodId: string; isEnabled?: boolean }>
): boolean {
  return wouldLeaveNoEnabledMethodsForOperation('withdrawal', rows, changes);
}

export function getMinMethodGuardMessage(
  rows: Array<{ provider: string; operation: string; methodId: string; isEnabled?: boolean }>,
  changes: Array<{ provider: string; operation: string; methodId: string; isEnabled?: boolean }>,
  supportedByKey?: SupportedByKeyMap
): string | null {
  const depositRows = filterRowsForShopMinMethodGuard(rows, supportedByKey, 'deposit');
  const withdrawalRows = filterRowsForShopMinMethodGuard(rows, supportedByKey, 'withdrawal');

  if (wouldLeaveNoEnabledDepositMethods(depositRows, changes)) {
    return SHOP_MIN_DEPOSIT_METHOD_MESSAGE;
  }
  if (wouldLeaveNoEnabledWithdrawalMethods(withdrawalRows, changes)) {
    return SHOP_MIN_WITHDRAWAL_METHOD_MESSAGE;
  }
  return null;
}

function catalogMethodRank(
  supportedByKey: Record<string, Array<{ methodId?: string; id?: string; sortOrder?: number }>>,
  provider: string,
  operation: string,
  methodId: string
): number {
  const methods = supportedByKey[toSupportedKey(provider, operation)] || [];
  const mid = String(methodId || '').trim().toLowerCase();
  const idx = methods.findIndex(
    (m) => String(m.methodId || m.id || '').trim().toLowerCase() === mid
  );
  if (idx < 0) return Number.MAX_SAFE_INTEGER;
  const sortOrder = methods[idx]?.sortOrder;
  return typeof sortOrder === 'number' ? sortOrder : idx;
}

/** Keep row order stable after toggles (catalog order, not API items order). */
export function sortProviderFeeRows<T extends { provider: string; operation: string; methodId: string }>(
  rows: T[],
  supportedByKey: Record<string, Array<{ methodId?: string; id?: string; sortOrder?: number }>>
): T[] {
  return [...rows].sort((a, b) => {
    const providerCmp = (a.provider || '').localeCompare(b.provider || '');
    if (providerCmp !== 0) return providerCmp;

    const operationCmp = String(a.operation || '').localeCompare(String(b.operation || ''));
    if (operationCmp !== 0) return operationCmp;

    const rankA = catalogMethodRank(supportedByKey, a.provider, a.operation, a.methodId);
    const rankB = catalogMethodRank(supportedByKey, b.provider, b.operation, b.methodId);
    if (rankA !== rankB) return rankA - rankB;

    return String(a.methodId || '').localeCompare(String(b.methodId || ''));
  });
}
